/**
 * Video URL helpers — turning a pasted YouTube, Vimeo or Wistia link into
 * something a player can load. Pure, so the admin server code that validates a
 * link and the components that play it read the same rules.
 *
 * Aliases: video url, embed url, youtube embed, vimeo embed, wistia embed
 */

/**
 * The media id in a Wistia link, or null when the link is not one. Accepts
 * the share and account links an admin copies (`*.wistia.com/medias/<id>`,
 * `/m/<id>`, `wi.st/medias/<id>`) and the iframe embed address
 * (`fast.wistia.net/embed/iframe/<id>`).
 */
export function wistiaMediaId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname;
    const isWistia =
      host === "wi.st" ||
      host.endsWith(".wi.st") ||
      host === "wistia.com" ||
      host.endsWith(".wistia.com") ||
      host === "wistia.net" ||
      host.endsWith(".wistia.net");
    if (!isWistia) return null;
    const match =
      /^\/(?:medias|m|embed\/iframe|embed\/medias)\/([a-z0-9]+)/i.exec(
        u.pathname,
      );
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/** A YouTube video id: letters, digits, `_` and `-`, at least 6 characters. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,}$/;

/** A Vimeo video path: digits only, e.g. `/76979871` or `/76979871/`. */
const VIMEO_ID = /^\/(\d+)\/?$/;

/** Short-link hosts: the video id is the first path segment. */
const YOUTUBE_SHORT_HOSTS = new Set(["youtu.be", "www.youtu.be"]);

/** Full-site hosts: the id is in `?v=` or after a path prefix below. */
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

/** Paths whose next segment is the id: `/shorts/ID`, `/live/ID`, `/embed/ID`, `/v/ID`. */
const YOUTUBE_ID_PATH = /^\/(?:shorts|live|embed|v)\/([^/]+)/;

function isYoutubeHost(hostname: string): boolean {
  return YOUTUBE_SHORT_HOSTS.has(hostname) || YOUTUBE_HOSTS.has(hostname);
}

/**
 * The video id in any YouTube link an admin or coach copies, or null if none
 * is present: share links (`youtu.be/ID?si=…`), watch links (`watch?v=ID`,
 * including `m.` and `music.`), Shorts, live, embed and `youtube-nocookie`
 * embed links.
 */
function youtubeId(u: URL): string | null {
  const id = YOUTUBE_SHORT_HOSTS.has(u.hostname)
    ? u.pathname.split("/")[1]
    : YOUTUBE_HOSTS.has(u.hostname)
      ? (u.searchParams.get("v") ?? YOUTUBE_ID_PATH.exec(u.pathname)?.[1])
      : null;
  return id && YOUTUBE_ID.test(id) ? id : null;
}

/**
 * The player address for a YouTube video id. The privacy-enhanced domain sets
 * no Google cookies until the visitor plays the video.
 */
export function youtubeEmbedUrl(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}`;
}

/** The address an `<iframe>` loads for this link, or null if none is known. */
export function toEmbedUrl(url: string): string | null {
  const wistiaId = wistiaMediaId(url);
  if (wistiaId) return `https://fast.wistia.net/embed/iframe/${wistiaId}`;
  try {
    const u = new URL(url);
    if (isYoutubeHost(u.hostname)) {
      const id = youtubeId(u);
      return id ? youtubeEmbedUrl(id) : null;
    }
    if (u.hostname.includes("vimeo.com")) {
      const match = VIMEO_ID.exec(u.pathname);
      return match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * The embed address set to start playing at once. Wistia spells the option
 * `autoPlay=true`; YouTube and Vimeo take `autoplay=1`. `playsinline=1` keeps
 * mobile playback in the page. Embed addresses from {@link toEmbedUrl} carry
 * no query string, so appending is safe.
 */
export function withAutoPlay(embedUrl: string): string {
  return embedUrl.startsWith("https://fast.wistia.net/")
    ? `${embedUrl}?autoPlay=true`
    : `${embedUrl}?autoplay=1&playsinline=1`;
}

/** Where YouTube serves a video's still images, one file per size. */
const YOUTUBE_THUMBNAIL_BASE = "https://img.youtube.com/vi/";

/** The widest a YouTube "no image" placeholder is (it is 120×90). */
const YOUTUBE_PLACEHOLDER_MAX_WIDTH = 120;

/**
 * Best-effort still image for a coach video URL. YouTube exposes predictable
 * thumbnail URLs (`maxresdefault` for HD, `hqdefault` as a guaranteed
 * fallback). Vimeo requires an API call and is intentionally not supported —
 * callers should fall back to another image (e.g. the coach's main image).
 */
export function getVideoThumbnailUrl(
  url: string,
): { src: string; fallback: string } | null {
  try {
    const u = new URL(url);
    const id = youtubeId(u);
    if (!id) return null;
    return {
      src: `${YOUTUBE_THUMBNAIL_BASE}${id}/maxresdefault.jpg`,
      fallback: `${YOUTUBE_THUMBNAIL_BASE}${id}/hqdefault.jpg`,
    };
  } catch {
    return null;
  }
}

/**
 * The images to try, in order, as a video's poster: the stored thumbnail when
 * there is one (Wistia fills it in), otherwise YouTube's HD then standard
 * still. Empty when neither exists; the caller shows its own fallback.
 */
export function videoPosterCandidates(video: {
  readonly url: string;
  readonly thumbnailUrl: string | null;
}): string[] {
  if (video.thumbnailUrl) return [video.thumbnailUrl];
  const youtube = getVideoThumbnailUrl(video.url);
  return youtube ? [youtube.src, youtube.fallback] : [];
}

/**
 * Whether a loaded image is YouTube's grey "no image" placeholder. YouTube
 * answers a size the video does not have (often `maxresdefault`, and every
 * size of a private video) with HTTP 404 and a valid 120×90 picture, so the
 * browser fires `load` rather than `error` and a poster must check the width.
 */
export function isYoutubePlaceholderThumbnail(
  src: string,
  naturalWidth: number,
): boolean {
  return (
    src.startsWith(YOUTUBE_THUMBNAIL_BASE) &&
    naturalWidth <= YOUTUBE_PLACEHOLDER_MAX_WIDTH
  );
}

/** A running time as a player shows it: `4:45`, `1:02:05`. */
export function formatVideoDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/**
 * Reads a running time typed as `4:45` or `1:02:05` back into seconds. Null
 * for anything else, including a blank field.
 */
export function parseVideoDuration(text: string): number | null {
  const match = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) return null;
  const [, h, m, s] = match;
  const minutes = Number(m);
  const seconds = Number(s);
  if (seconds > 59 || (h !== undefined && minutes > 59)) return null;
  const total = Number(h ?? 0) * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : null;
}
